import {
  forwardRef,
  useEffect,
  useRef,
  AudioHTMLAttributes,
  ReactEventHandler,
} from "react";

import { useMediaCache } from "@/hooks/use-media-cache";
import { useLockedMediaSrc } from "@/hooks/use-locked-media-src";
import { onNextUserGesture } from "@/utils/user-gesture";

interface AudioPlayerProps {
  src?: string;
  autoPlay?: boolean;
  loop?: boolean;
  onEnded?: ReactEventHandler<HTMLAudioElement>;
  playing?: boolean; // Added for consistency with VideoPlayer
}

export const AudioPlayer = forwardRef<
  HTMLAudioElement,
  AudioHTMLAttributes<HTMLAudioElement> & AudioPlayerProps
>(
  (
    { src, autoPlay = false, loop = false, onEnded, playing, ...props },
    forwardedRef,
  ) => {
    const blobUrl = useMediaCache(src || null, "audio");
    const internalRef = useRef<HTMLAudioElement>(null);

    // Use forwarded ref if provided, otherwise use internal ref
    const audioRef =
      (forwardedRef as React.RefObject<HTMLAudioElement>) || internalRef;

    // Whether playback of the current clip has been requested yet. Upgrading
    // the source to the cached blob is only safe before that - afterwards a
    // src swap reloads the element and drops playback on the floor.
    const hasStartedRef = useRef(false);
    const clipKeyRef = useRef(src);
    if (clipKeyRef.current !== src) {
      clipKeyRef.current = src;
      hasStartedRef.current = false;
    }

    const mediaSrc = useLockedMediaSrc(src, blobUrl, !hasStartedRef.current);

    useEffect(() => {
      const audio = audioRef?.current;
      if (!audio) return;

      const handleStarted = () => {
        hasStartedRef.current = true;
      };

      // "play" fires as soon as playback is requested, "playing" once audio
      // actually runs. Listening to both closes the buffering window between
      // them, where a src upgrade would otherwise silently stop playback.
      audio.addEventListener("play", handleStarted);
      audio.addEventListener("playing", handleStarted);

      return () => {
        audio.removeEventListener("play", handleStarted);
        audio.removeEventListener("playing", handleStarted);
      };
    }, [audioRef]);

    // Handle autoPlay or external playing prop. If iOS blocks un-muted
    // autoplay (no user activation), retry inside the next tap.
    useEffect(() => {
      const audio = audioRef?.current;
      if (!audio || !mediaSrc) return;

      // Uncontrolled: no playing prop and no autoPlay means the parent drives
      // this element itself through the ref (song detail page). Leave it
      // alone - pausing here would stop audio the parent just started.
      if (playing === undefined && !autoPlay) return;

      const shouldPlay = playing !== undefined ? playing : autoPlay;

      if (!shouldPlay) {
        audio.pause();
        return;
      }

      let cancelGestureRetry: (() => void) | undefined;

      const attemptPlay = () => {
        audio.play().catch((error: DOMException) => {
          if (error.name === "NotAllowedError") {
            cancelGestureRetry = onNextUserGesture(attemptPlay);
          } else if (error.name !== "AbortError") {
            console.error("Error playing audio:", error);
          }
        });
      };

      attemptPlay();

      return () => cancelGestureRetry?.();
    }, [autoPlay, mediaSrc, audioRef, playing]);

    return (
      <audio
        src={mediaSrc}
        ref={audioRef}
        loop={loop}
        onEnded={onEnded}
        {...props}
      />
    );
  },
);

AudioPlayer.displayName = "AudioPlayer";
